import { useEffect, useRef, useState } from 'preact/hooks';
import { initialState, type WidgetState } from './state';
import { Step1Address } from './steps/Step1Address';
import { Step2Contact } from './steps/Step2Contact';
import { Step3Result } from './steps/Step3Result';
import { RoofAerial } from './RoofAerial';
import { estimate, resolveAddress, suggest } from './lib/api';

/** What every step component receives. Steps land in Tasks 19 to 21. */
export type StepProps = {
  state: WidgetState;
  update: (patch: Partial<WidgetState>) => void;
};

/**
 * postMessage type the embed loader listens for.
 *
 * MUST MATCH `RESIZE_TYPE` in public/loader.js. The loader drops any message
 * whose type it does not recognise, so a mismatch here is silent: the iframe
 * simply never resizes and stays at its 560px seed height. There is no shared
 * constant because the loader is dependency-free ES5 served verbatim.
 */
const RESIZE_MESSAGE = 'ks-studio:resize';

/**
 * The three-step widget shell. Renders its own chrome standalone, the host
 * page's in embed mode.
 *
 * TASK 18 SHIPS THE SHELL ONLY. The three step components are Tasks 19, 20 and
 * 21; until they land each renders as a labelled placeholder, which is enough to
 * prove the mount, the chrome, the focus move and the resize protocol work.
 */
export function App() {
  const [state, setState] = useState<WidgetState>(initialState);
  const update = (patch: Partial<WidgetState>) => setState((s) => ({ ...s, ...patch }));

  const widgetRef = useRef<HTMLElement>(null);
  const mounted = useRef(false);

  // Embedded on the marketing site (loader.js appends ?embed=1): render just the
  // form, because the host page already supplies the Kasselman header/footer.
  // Same flag the analytics context reads.
  const embed =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('embed');

  // Focus management. On each step change (not the initial mount) move focus to
  // the new step's heading, so a keyboard user is not dropped back at the top of
  // the document and a screen reader announces the screen they landed on.
  // preventScroll keeps the embedding host page from jumping. tabindex=-1 makes
  // the heading a scripted focus target without adding it to the tab order.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const heading = widgetRef.current?.querySelector<HTMLElement>('h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, [state.step]);

  // Drop the page background in embed mode so the host page shows through.
  useEffect(() => {
    if (embed) document.body.classList.add('embed-body');
    return () => document.body.classList.remove('embed-body');
  }, [embed]);

  /**
   * Auto-resize the embedding iframe.
   *
   * Guarded on being framed at all: unframed there is no parent to tell, and the
   * guard is also what keeps ResizeObserver (absent in jsdom) out of the tests.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;
    if (typeof ResizeObserver === 'undefined') return;

    const post = () => {
      // BODY, NOT documentElement. Carried over from dac-qualifier, where this
      // is its ADR-0019 (no relation to this repo's ADR-0019): documentElement's
      // height is floored at the iframe's own viewport, so measuring it reports
      // the frame's height back to itself and the protocol becomes a one-way
      // ratchet that can only ever grow. `display: flow-root` on body in
      // styles.css holds the margin containment that measurement needs.
      const height = Math.ceil(document.body.scrollHeight);
      window.parent.postMessage({ type: RESIZE_MESSAGE, height }, '*');
    };

    post();
    // Observing body, not documentElement, for the same reason. documentElement's
    // box is the viewport, so it does not resize when the content does, and this
    // observer would go quiet exactly when it needs to fire.
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, []);

  return (
    <div class={embed ? 'page embed' : 'page'}>
      {!embed && (
        <header class="site-header">
          <a href="https://kasselmansolar.com" target="_blank" rel="noopener noreferrer">
            <img class="site-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          </a>
          <p class="site-tagline">
            Reputation forged by results, relationships built on trust.
          </p>
        </header>
      )}

      <main class="widget" ref={widgetRef}>
        {embed && (
          <header class="widget-header">
            <img
              class="widget-logo"
              src="/kasselman-full-logo.png"
              alt="Kasselman Solar"
            />
          </header>
        )}

        {/* PLACEHOLDER COPY, NOT REVIEWED. dac-qualifier's h1 is reviewed legal
            copy about a battery incentive and pinned to a dated review record;
            none of it is true of Studio, so none of it was carried over.

            Whatever replaces this is bound by the Stage 1 definition of done:
            no dollar figure, no currency symbol, no savings claim anywhere in
            web/ (ADR-0016). This sentence is written to that bar, but it has had
            no legal review and Studio has no automated copy gate to catch one
            that is not. */}
        <h1>See how much solar your roof can fit.</h1>

        {state.step === 'address' && (
          <Step1Address
            state={state}
            update={update}
            suggest={suggest}
            resolve={resolveAddress}
          />
        )}
        {state.step === 'contact' && (
          <Step2Contact state={state} update={update} submit={estimate} />
        )}
        {state.step === 'result' && (
          <Step3Result
            state={state}
            update={update}
            aerial={
              state.resolved && (
                <RoofAerial
                  lat={state.resolved.lat}
                  lng={state.resolved.lng}
                  address={state.resolved.formatted}
                />
              )
            }
          />
        )}
      </main>

      {!embed && (
        <footer class="site-footer">
          <img class="footer-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          <p class="footer-contact">
            <a href="tel:+15184788365">Upstate (518) 478-8365</a>
            <span class="sep" aria-hidden="true">
              ·
            </span>
            <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
            <span class="sep" aria-hidden="true">
              ·
            </span>
            <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
          </p>
          <p class="footer-creds">
            4,000+ solar installations across New York · NYSERDA Platinum Quality Solar
            Installer
          </p>
        </footer>
      )}
    </div>
  );
}

/**
 * Stands in for a step component until its task lands. Step 3 is Task 21; steps 1
 * and 2 are real as of Tasks 19 and 20.
 *
 * Renders an `h2` deliberately: the focus effect above looks for one, so the
 * placeholders keep that behaviour exercised rather than leaving it untested
 * until Task 21.
 */
function StepPlaceholder({ step, task }: { step: string; task: number }) {
  return (
    <section>
      <h2>Step: {step}</h2>
      <p>Not built yet. This screen lands in Task {task}.</p>
    </section>
  );
}
